/* eslint no-param-reassign: 0 */
import {
  DirectoryGroupingConsts,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { isEqual } from 'lodash';
import Vue from 'vue';

const { memberTypes } = DirectoryGroupingConsts;

/**
 * This method handles a special case where the SystemUserCollection should be queried for items
 * to bind or unbind when the targetType is "user". But because there is already a UserCollection
 * used for administrators, named as such because it uses the /api/users endpoint, this special
 * case can't be handled in the StoreModuleMap itself. And it makes more sense to do it here since
 * admin users are not bound/unbound to other items.
 */
export const getStoreModuleType = (targetType) => {
  if (targetType === memberTypes.user) {
    return ItemTypeConsts.systemUser;
  }

  return targetType;
};

/**
 * This mixin provides actions, getters and mutations that can be mixed in to a
 * Vuex model used in an Aside Panel, e.g. Microsoft 365 Directory Aside Panel,
 * System User Aside Panel, Add Organization etc. to manage the state of the item
 */
export default {
  actions: {
    bindAssociations(context, { parentType, targetType }) {
      const { id } = context.getters;
      const selectedTargetIds = context.getters.getTargetsToBind(targetType);
      const unselectedTargetIds = context.getters.getTargetsToUnbind(targetType);

      const directoryGroupingPayload = {
        parentType,
        parentId: id,
        targetType,
        selectedTargetIds,
        unselectedTargetIds,
      };

      return context.dispatch(
        'DirectoryGroupingModule/bindAssociations',
        directoryGroupingPayload,
        { root: true },
      );
    },

    /**
     * Reset the item in the state. Note that the model that mixes this action
     * in is responsible for implementing the RESET_ITEM mutation! This is
     * required because the state is not generic enough across the different
     * models to be reset completely here.
     * @todo Consider renaming this method reset state because the mutation
     * resets the entire state, not just the item!
     */
    resetItem({ commit }) {
      commit('RESET_ITEM');
    },

    /**
     * @deprecated please use updateItem
     */
    setItemField({ commit }, { key, value }) {
      commit('SET_ITEM_FIELD', { key, value });
    },

    /**
     * this can be used to update an item,
     * or a property of an item
     * @example
     * this.updateItem({ email: 'example@email.com' })
     * or
     * this.updateItem({ email: 'example@email.com`, firstname: 'first', lastname: 'last' })
     */
    updateItem({ commit }, item) {
      commit('SET_ITEM', item);
    },

    setItemStateToCreate({ commit }) {
      commit('SET_ITEM_STATE', { create: true });
    },

    setItemStateToUpdate({ commit }) {
      commit('SET_ITEM_STATE', { update: true });
    },

    updatePreviousItem({ commit }, item) {
      commit('SET_PREVIOUS_ITEM', item);
    },

    setPreviousItemField({ commit }, { key, value }) {
      commit('SET_PREVIOUS_ITEM_FIELD', { key, value });
    },

    unset({ commit }, payload) {
      commit('UNSET', payload);
    },
  },

  getters: {
    getTargetsToBind(state, getters, rootState, rootGetters) {
      return (targetType) => {
        const type = getStoreModuleType(targetType);
        const module = StoreModuleMap[type];
        return rootGetters[`${module}/getItemsToBind`].map(item => item.id);
      };
    },

    getTargetsToUnbind(state, getters, rootState, rootGetters) {
      return (targetType) => {
        const type = getStoreModuleType(targetType);
        const module = StoreModuleMap[type];
        return rootGetters[`${module}/getItemsToUnbind`].map(item => item.id);
      };
    },

    /**
     * JSON.parse(JSON.stringify()) is used here to to convert the Vue objects
     * to regular JSON objects and remove Vue's observers. Observers in nested
     * objects in the state, such as on a phone number object in an array of
     * phone numbers, will incorrectly return false when compared with lodash's
     * isEqual() method, even if when evaluated they both have the same values.
     * @see https://github.com/vuejs/Discussion/issues/292#issuecomment-122825813
     * @see https://www.reddit.com/r/vuejs/comments/5p96hy/what_really_is_object_ob_observer/
     */
    hasItemChanged(state = { item: {}, previousItem: {} }) {
      const item = JSON.parse(JSON.stringify(state.item));
      const previousItem = JSON.parse(JSON.stringify(state.previousItem));
      return !isEqual(item, previousItem);
    },

    id(state = { item: {} }, getters = { idField: {} }) {
      return state.item[getters.idField];
    },

    /**
     * @todo Prefer using the mapState() helper in child components!
     * @see https://vuex.vuejs.org/guide/state.html#the-mapstate-helper
     */
    item(state = { item: {} }) {
      return state.item;
    },

    isItemBeingCreated(state = { itemState: {} }) {
      return state.itemState.create === true;
    },

    isItemBeingUpdated(state = { itemState: {} }) {
      return state.itemState.update === true;
    },
  },

  mutations: {
    /**
     * Note that Vue.set() is used here in case the key doesn't yet exist in
     * the default state!
     */
    SET_ITEM(state = { item: {} }, item = {}) {
      // TODO: Is there a better way to set state.item from _all_ the fields in the item?
      Object.keys(item).forEach(key => Vue.set(state.item, key, item[key]));
    },

    SET_ITEM_FIELD(state = { item: {} }, { key, value }) {
      // Use Vue.set() here in case the key doesn't exist in the default state!
      Vue.set(state.item, key, value);
    },

    SET_ITEM_STATE(state = { itemState: {} }, { create, update }) {
      state.itemState.create = create;
      state.itemState.update = update;
    },

    SET_PREVIOUS_ITEM(state = { previousItem: {} }, item = {}) {
      // TODO: Is there a better way to set state.item from _all_ the fields in the item?
      Object.keys(item).forEach(key => Vue.set(state.previousItem, key, item[key]));
    },

    SET_PREVIOUS_ITEM_FIELD(state = { previousItem: {} }, { key, value }) {
      Vue.set(state.previousItem, key, value);
    },

    UNSET(state = { item: {} }, { key, keys = [] }) {
      if (key) {
        Vue.delete(state.item, key);
      }

      keys.map(k => Vue.delete(state.item, k));
    },
  },
};
